/**
 * @file PageDraftSummary.tsx
 * @description Верх панели «Публикации» (phase19.16 Р3а; uiux-r3 §3.1):
 * «На сайте: версия 7 · 12.10 14:32 · Иван», «Черновик: 3 правки · Анна,
 * 5 мин назад», [Что изменено ›] (правки по блокам) и [Отменить правки…] с
 * подтверждением «Вернуть страницу к версии на сайте? Пропадут 3 правки
 * Анны (5 мин назад). Сайт не изменится». «Отменить правки» — право
 * контента (SB126); страница, которой ещё не было на сайте, — без отмены.
 */

import { ChevronRight, Undo2 } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { PageDiffList } from "./PageDiffList";
import type { SitePageChangesDto } from "../../../lib/api-site-page-publish";
import { pagePublishErrorText } from "../../../lib/site-builder/page-publish-errors";
import { pluralRu } from "../../../lib/plural";
import { draftEditCount, draftLine, editorLabel, editsWord, liveLine, timeAgo } from "../../../lib/site-builder/page-status";
import { cn } from "@/lib/utils";

export function PageDraftSummary({ changes }: { changes: SitePageChangesDto }) {
  const b = useBuilder();
  const view = useSiteView();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const edits = changes.hasChanges ? Math.max(1, draftEditCount(changes)) : 0;
  const canDiscard = b.canEditPages && changes.hasChanges && changes.live !== null;

  async function onDiscard() {
    const who = editorLabel(changes.draft.editedBy, view.currentUserId);
    const whose = who && who !== "вы" ? ` (${who}${changes.draft.editedAt ? `, ${timeAgo(changes.draft.editedAt)}` : ""})` : "";
    const ok = await b.confirm({
      title: "Вернуть страницу к версии на сайте?",
      description: `${pluralRu(edits, ["Пропадёт", "Пропадут", "Пропадут"])} ${editsWord(edits)}${whose}. Сайт не изменится.`,
      action: "Отменить правки",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await b.publishing.discard();
      notify.success("Правки отменены — черновик совпадает с сайтом");
    } catch (err) {
      notify.error(pagePublishErrorText(err, "Не удалось отменить правки — черновик не изменён."));
      void b.publishing.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="type-body text-on-surface">{liveLine(changes.live)}</p>
      {changes.live ? <p className="type-body text-on-surface">{draftLine(edits, changes.draft, view.currentUserId)}</p> : null}
      <div className="flex flex-wrap gap-2">
        {changes.hasChanges && changes.diff.items.length > 0 ? (
          <Button variant="outline" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            Что изменено
            <ChevronRight aria-hidden className={cn("transition-transform", open && "rotate-90")} />
          </Button>
        ) : null}
        {canDiscard ? (
          <Button variant="ghost" className="text-destructive" loading={busy} onClick={() => void onDiscard()}>
            <Undo2 aria-hidden />
            Отменить правки…
          </Button>
        ) : null}
      </div>
      {open ? <PageDiffList items={changes.diff.items} /> : null}
    </div>
  );
}
